import { useMemo, useEffect } from 'react';
import TimeSeries, {
  Line,
  Range,
  Waypoints,
  Key,
  DataTable,
  Title,
  Value,
} from '@/components/TimeSeries';
import { useTimeSeriesReducer } from '@/components/TimeSeries/hooks';
import { useChartDefinitionsContext } from '@/components/ChartDefinitionsContext';
import { toCurrencyTruncated } from '@/utilities/formatting';
import { DataTableWrapper, Dot } from './styled';
import { useTheme } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';

enum Series {
  series01 = 'EPS',
  series02 = "analysts' EPS range",
  rangePastLow = 'analystsPastEPSRangeLow',
  rangePastHigh = 'analystsPastEPSRangeHigh',
  rangeFutureLow = 'analystsFutureEPSRangeLow',
  rangeFutureHigh = 'analystsFutureEPSRangeHigh',
}

type DefaultFilter = {
  [key in Series]: boolean;
};

interface Props {
  EPS: number[][];
  EPSMax: number;
  EPSMin: number;
  EPSAnalysts: {
    [date: number]: number;
  };
  EPSAnalystConfirmation: {
    [date: number]: number;
  };
  analystsEPSRangeMax: number;
  analystsEPSRangeMin: number;
  analystsPastEPSRangeLow: number[][];
  analystsPastEPSRangeHigh: number[][];
  analystsFutureEPSRangeLow: number[][];
  analystsFutureEPSRangeHigh: number[][];
  currencyISO: string;
  dayZero: number;
  defaultFilter?: DefaultFilter;
  hideToggleFilter?: boolean;
  labels?: {
    past: string;
    future: string;
    seriesEPS: string;
    seriesEPSRange: string;
    seriesEPSRangeAnalystCount: (count: number) => string;
    seriesEPSRangeLastConfirmed: (date: Date) => string;
    seriesDataPointDate: (date: Date) => string;
    notAvailable: string;
  };
}

const EarningsPerShareGrowth = ({
  EPS,
  EPSMax,
  EPSMin,
  EPSAnalysts,
  EPSAnalystConfirmation,
  analystsEPSRangeMax,
  analystsEPSRangeMin,
  analystsPastEPSRangeLow,
  analystsPastEPSRangeHigh,
  analystsFutureEPSRangeLow,
  analystsFutureEPSRangeHigh,
  currencyISO,
  dayZero,
  defaultFilter,
  hideToggleFilter,
  labels = {
    past: 'Actual',
    future: 'Analysts Forecasts',
    seriesEPS: 'EPS',
    seriesEPSRange: "analysts' EPS range",
    seriesEPSRangeAnalystCount: (count) => `${count} Analysts`,
    seriesEPSRangeLastConfirmed: (date) =>
      `Last confirmed on ${date.toDateString()}`,
    seriesDataPointDate: (date) => date.toDateString(),
    notAvailable: 'n/a',
  },
}: Props) => {
  const theme = useTheme();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'chart',
    modifier: 'EarningsPerShareGrowth',
  });

  const {
    chart01Gradient01,
    chart02Gradient02,
    chartNEGGradient01,
    chartNEGGradient02,
  } = useChartDefinitionsContext();

  const data = useMemo(
    () => ({
      [Series.series01]: EPS,
      [Series.series02]: [],
      [Series.rangePastLow]: analystsPastEPSRangeLow,
      [Series.rangePastHigh]: analystsPastEPSRangeHigh,
      [Series.rangeFutureLow]: analystsFutureEPSRangeLow,
      [Series.rangeFutureHigh]: analystsFutureEPSRangeHigh,
    }),
    [
      EPS,
      analystsPastEPSRangeLow,
      analystsPastEPSRangeHigh,
      analystsFutureEPSRangeLow,
      analystsFutureEPSRangeHigh,
    ]
  );

  const filterForPrioritySeries = useMemo(() => {
    return function (n: string) {
      if (hideToggleFilter && typeof defaultFilter !== 'undefined') {
        if (defaultFilter['EPS'] && n === Series.series01) {
          return true;
        }
        if (
          defaultFilter[Series.series02] &&
          defaultFilter[Series.rangePastLow] &&
          defaultFilter[Series.rangePastHigh] &&
          defaultFilter[Series.rangeFutureLow] &&
          defaultFilter[Series.rangeFutureHigh] &&
          n === Series.series02
        ) {
          return true;
        }
        return false;
      }
      return n === Series.series01 || n === Series.series02;
    };
  }, [defaultFilter, hideToggleFilter]);

  const minMaxY = {
    [Series.series01]: [EPSMin, EPSMax],
    [Series.series02]: [analystsEPSRangeMin, analystsEPSRangeMax],
  };

  const {
    entities,
    keys,
    minimumX,
    maximumX,
    periods,
    series,
    filter,
    disabled,
    dispatch,
  } = useTimeSeriesReducer<Series>({
    data,
    dayZero,
    defaultFilter: defaultFilter as DefaultFilter,
    hideToggleFilter,
  });

  useEffect(
    () =>
      dispatch({
        type: 'reset',
        payload: {
          data,
          dayZero,
          defaultFilter: defaultFilter as DefaultFilter,
        },
      }),
    [dispatch, data, dayZero]
  );

  const colors = {
    [Series.series02]: {
      fillPositive: chart01Gradient01,
      fillNegative: chartNEGGradient01,
      fillFuturePositive: chart02Gradient02,
      fillFutureNegative: chartNEGGradient02,
    },
  };

  const minimumY = Math.min(
    ...series
      .filter(filterForPrioritySeries)
      .filter((n) => filter[n])
      .map((n) => minMaxY[n][0])
  );
  const maximumY = Math.max(
    ...series
      .filter(filterForPrioritySeries)
      .filter((n) => filter[n])
      .map((n) => minMaxY[n][1])
  );

  const seriesWithLabels = useMemo(
    () => [
      { key: Series.series01, label: labels.seriesEPS },
      { key: Series.series02, label: labels.seriesEPSRange },
      { key: Series.rangePastLow, label: 'analystsPastEPSRangeLow' },
      { key: Series.rangePastHigh, label: 'analystsPastEPSRangeHigh' },
      { key: Series.rangeFutureLow, label: 'analystsFutureEPSRangeLow' },
      { key: Series.rangeFutureHigh, label: 'analystsFutureEPSRangeHigh' },
    ],
    [labels.seriesEPS, labels.seriesEPSRange]
  );

  return (
    <Track>
      <TimeSeries
        keys={keys}
        filter={filter}
        disabled={disabled}
        series={seriesWithLabels}
        periods={periods}
        entities={entities}
        minimumX={minimumX}
        maximumX={maximumX}
        minimumY={minimumY}
        maximumY={maximumY}
        pastLabel={labels.past}
        futureLabel={labels.future}
        formatYAxis={(value) => toCurrencyTruncated(value, 0, currencyISO)}
        legend={() =>
          series.filter(filterForPrioritySeries).map((series) => {
            let disable = disabled[series];
            if (series === Series.series02) {
              disable =
                disabled[Series.rangePastLow] &&
                disabled[Series.rangePastHigh] &&
                disabled[Series.rangeFutureLow] &&
                disabled[Series.rangeFutureHigh];
            }
            return (
              <Key
                key={`timeseries-key-${series}`}
                disabled={disable}
                active={filter[series] && !disable}
                onClick={() => {
                  let key: string | string[] = series;
                  if (series === Series.series02) {
                    key = [
                      Series.series02,
                      Series.rangePastLow,
                      Series.rangePastHigh,
                      Series.rangeFutureLow,
                      Series.rangeFutureHigh,
                    ];
                  }
                  trackEvent({
                    legend: series,
                    action: 'click',
                    isVisible: !filter[series],
                  });
                  dispatch({
                    type: 'filter',
                    payload: {
                      key,
                      data,
                    },
                  });
                }}
              >
                <Dot
                  color={theme.color.chart01}
                  colorFuture={theme.color.chart02}
                  active={filter[series] && !disable}
                  opacity={series === Series.series02 ? 0.5 : 1}
                />
                <span>
                  {series === Series.series01
                    ? labels.seriesEPS
                    : labels.seriesEPSRange}
                </span>
              </Key>
            );
          })
        }
        marker={({ key, xScale, yScale }) => {
          const isFuture = key > dayZero;
          const rangeHigh = isFuture
            ? Series.rangeFutureHigh
            : Series.rangePastHigh;
          const rangeLow = isFuture
            ? Series.rangeFutureLow
            : Series.rangePastLow;
          const color = isFuture ? theme.color.chart02 : theme.color.chart01;
          const entity = entities[key];
          const x = xScale(key);
          return (
            entity &&
            series
              .filter(filterForPrioritySeries)
              .filter((n) => filter[n])
              .filter(
                (n) =>
                  (n === Series.series02 &&
                    entity[rangeHigh] !== undefined &&
                    entity[rangeLow] !== undefined) ||
                  (n !== Series.series02 && entity[n] !== undefined)
              )
              .map((series) => {
                let value: number[] = [];
                if (series === Series.series02) {
                  value = [entity[rangeHigh], entity[rangeLow]];
                }
                if (series !== Series.series02) {
                  value = [entity[series]];
                }
                return (
                  <g key={`data-point-marker-group-${series}-${x}`}>
                    {value.map((n, i) => {
                      const y = yScale(n);
                      return (
                        <circle
                          key={`data-point-marker-${series}-${y}-${i}`}
                          transform={`translate(${x} ${y})`}
                          r="4"
                          fill={color}
                          stroke="white"
                          strokeWidth="1"
                        />
                      );
                    })}
                  </g>
                );
              })
          );
        }}
        dataTable={({ key }) => {
          const analystCount = EPSAnalysts[key];
          const analystConfirmation = EPSAnalystConfirmation[key];
          const isFuture = key > dayZero;
          const rangeHigh = isFuture
            ? Series.rangeFutureHigh
            : Series.rangePastHigh;
          const rangeLow = isFuture
            ? Series.rangeFutureLow
            : Series.rangePastLow;
          const entity = entities[key];
          return (
            <DataTableWrapper>
              <DataTable noWrap>
                <thead>
                  <tr>
                    <th colSpan={2}>
                      {key
                        ? labels.seriesDataPointDate(new Date(key))
                        : labels.notAvailable}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {series
                    .filter(filterForPrioritySeries)
                    .filter((n) => filter[n])
                    .map((series) => {
                      let value: number[] | undefined = undefined;
                      if (entity !== undefined) {
                        if (
                          series === Series.series02 &&
                          entity[rangeHigh] !== undefined &&
                          entity[rangeLow] !== undefined
                        ) {
                          value = [entity[rangeLow], entity[rangeHigh]];
                        }
                        if (
                          series !== Series.series02 &&
                          entity[series] !== undefined
                        ) {
                          value = [entity[series]];
                        }
                      }
                      return (
                        <tr key={`data-table-row-${series}`}>
                          <Title
                            data-cy-id={`chart-earnings-per-share-growth-datatable-title-${series}`}
                          >
                            {series === Series.series01
                              ? labels.seriesEPS
                              : labels.seriesEPSRange}
                          </Title>
                          <Value
                            data-cy-id={`chart-earnings-per-share-growth-datatable-value-${series}`}
                            noData={value === undefined}
                          >
                            <div>
                              {value !== undefined &&
                                value.map((n, i) => (
                                  <span key={`data-table-value-${i}-${n}`}>
                                    <span>{i > 0 && ` - `}</span>
                                    <Value
                                      as="span"
                                      colorPos={
                                        isFuture
                                          ? theme.color.chart02
                                          : theme.color.chart01
                                      }
                                      colorNeg={theme.color.bad}
                                      value={n}
                                    >
                                      {toCurrencyTruncated(n, 3, currencyISO)}
                                    </Value>
                                  </span>
                                ))}
                            </div>
                            {series === Series.series02 && (
                              <Value as="div">
                                <span>
                                  {analystCount !== undefined &&
                                    analystCount > 0 &&
                                    labels.seriesEPSRangeAnalystCount(
                                      analystCount
                                    )}
                                </span>
                                <span>&nbsp;</span>
                              </Value>
                            )}
                            {series === Series.series02 && (
                              <Value as="div">
                                <span>
                                  {isFuture &&
                                    analystConfirmation !== undefined &&
                                    analystConfirmation > 0 &&
                                    labels.seriesEPSRangeLastConfirmed(
                                      new Date(analystConfirmation)
                                    )}
                                </span>
                                <span>&nbsp;</span>
                              </Value>
                            )}
                          </Value>
                        </tr>
                      );
                    })}
                </tbody>
              </DataTable>
            </DataTableWrapper>
          );
        }}
      >
        {({ xScale, yScale, maximumX, zeroX, keys, entities }) =>
          series
            .filter(filterForPrioritySeries)
            .slice()
            .reverse()
            .filter((n) => filter[n])
            .map((key) => {
              return (
                <g key={`chart-${key}`}>
                  {key === Series.series02 && (
                    <>
                      <Range
                        data={keys.filter(
                          (d) =>
                            entities[d][Series.rangePastHigh] !== undefined &&
                            entities[d][Series.rangePastLow] !== undefined
                        )}
                        xSelect={(d) => d}
                        ySelectLow={(d) => entities[d][Series.rangePastLow]}
                        ySelectHigh={(d) => entities[d][Series.rangePastHigh]}
                        xScale={xScale}
                        yScale={yScale}
                        fillPos={colors[Series.series02].fillPositive}
                        fillNeg={colors[Series.series02].fillNegative}
                        endX={zeroX}
                      />
                      <Range
                        data={keys.filter(
                          (d) =>
                            entities[d][Series.rangeFutureHigh] !== undefined &&
                            entities[d][Series.rangeFutureLow] !== undefined
                        )}
                        xSelect={(d) => d}
                        ySelectLow={(d) => entities[d][Series.rangeFutureLow]}
                        ySelectHigh={(d) => entities[d][Series.rangeFutureHigh]}
                        xScale={xScale}
                        yScale={yScale}
                        fillPos={colors[Series.series02].fillFuturePositive}
                        fillNeg={colors[Series.series02].fillFutureNegative}
                      />
                    </>
                  )}
                  {key === Series.series01 && (
                    <>
                      <Line
                        data={keys.filter(
                          (d) => entities[d][key] !== undefined
                        )}
                        xSelect={(d) => d}
                        ySelect={(d) => entities[d][key]}
                        xScale={xScale}
                        yScale={yScale}
                        strokePos={theme.color.chart01}
                        strokeNeg={theme.color.bad}
                        endX={zeroX}
                      />
                      {zeroX !== maximumX && (
                        <Line
                          data={keys.filter(
                            (d) => entities[d][key] !== undefined
                          )}
                          xSelect={(d) => d}
                          ySelect={(d) => entities[d][key]}
                          xScale={xScale}
                          yScale={yScale}
                          strokePos={theme.color.chart02}
                          strokeNeg={theme.color.bad}
                          startX={zeroX}
                        />
                      )}
                      {zeroX !== maximumX && (
                        <Waypoints
                          data={keys.filter(
                            (d) => entities[d][key] !== undefined
                          )}
                          xSelect={(d) => d}
                          ySelect={(d) => entities[d][key]}
                          xScale={xScale}
                          yScale={yScale}
                          startX={zeroX}
                          fill={theme.color.chart02}
                        />
                      )}
                      <Waypoints
                        data={keys.filter(
                          (d) => entities[d][key] !== undefined
                        )}
                        xSelect={(d) => d}
                        ySelect={(d) => entities[d][key]}
                        xScale={xScale}
                        yScale={yScale}
                        endX={zeroX}
                        fill={theme.color.chart01}
                      />
                    </>
                  )}
                </g>
              );
            })
        }
      </TimeSeries>
    </Track>
  );
};

EarningsPerShareGrowth.defaultProps = {
  defaultFilter: {
    [Series.series01]: true,
    [Series.series02]: true,
    [Series.rangePastLow]: true,
    [Series.rangePastHigh]: true,
    [Series.rangeFutureLow]: true,
    [Series.rangeFutureHigh]: true,
  },
  hideToggleFilter: false,
};

export default EarningsPerShareGrowth;
